import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../lib/api';

export const money = (value, dash = '—') =>
  typeof value === 'number'
    ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })
    : dash;

export const rate = (value) => (typeof value === 'number' ? `$${value.toFixed(4)}` : '—');

export const errorText = (err, fallback = 'That action could not be completed') => {
  const raw = err?.message || '';
  try {
    const parsed = JSON.parse(raw);
    return parsed.message || fallback;
  } catch {
    return raw || fallback;
  }
};

const TONES = {
  good: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30',
  info: 'bg-sky-500/10 text-sky-700 border-sky-500/30',
  warn: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  bad: 'bg-rose-500/10 text-rose-700 border-rose-500/30',
  muted: 'bg-muted text-muted-foreground border-transparent',
};
const STATUS_TONE = {
  DUE: 'warn', VOID: 'muted', REFUNDED: 'muted', REFUNDING: 'warn',
  VERIFIED: 'good', ACTIVE: 'good', FINAL: 'good', APPROVED: 'info', MATCH: 'good', SETTLED: 'good', PAID: 'good',
  SUBMITTED_FOR_APPROVAL: 'info', READY_FOR_PAYMENT: 'info', PROCESSING: 'info',
  EXPECTED_DIFFERENCE: 'info', NEEDS_REVIEW: 'warn', PENDING: 'warn',
  BANK_VERIFICATION_PENDING: 'warn', REQUIRES_REVIEW: 'warn', DATA_QUALITY: 'warn',
  LEGACY_BUG: 'warn', RETURNED_FOR_CORRECTION: 'bad', REJECTED: 'bad', STEP9_BUG: 'bad',
  PAYMENT_PROFILE_REQUIRED: 'bad', PAYMENT_PROFILE_REJECTED: 'bad', RETURNED: 'bad', FAILED: 'bad',
};

export const StatusPill = ({ value, testid }) => (
  <span data-testid={testid}
        className={`inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium tracking-wide
                    ${TONES[STATUS_TONE[String(value || '').toUpperCase()] || 'muted']}`}>
    {String(value || '—').replace(/_/g, ' ')}
  </span>
);

export const Card = ({ title, aside, children, testid, className = '' }) => (
  <section className={`min-w-0 rounded-xl border bg-card ${className}`} data-testid={testid}>
    {title && (
      <div className="flex items-center justify-between gap-3 border-b px-5 py-4">
        <h2 className="text-base md:text-lg font-medium">{title}</h2>
        {aside}
      </div>
    )}
    {children}
  </section>
);

export const PageHeader = ({ title, subtitle, children, eyebrow = 'Finance' }) => (
  <header className="flex flex-wrap items-end justify-between gap-4">
    <div className="max-w-2xl">
      <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">{eyebrow}</p>
      <h1 className="mt-1 text-4xl sm:text-5xl font-semibold tracking-tight">{title}</h1>
      {subtitle && <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>}
    </div>
    <div className="flex flex-wrap items-end gap-3">{children}</div>
  </header>
);

// Each call starts a request; the returned check is true only while it is still the latest one.
export function useLatestRequest() {
  const ref = useRef(0);
  return useCallback(() => { const id = ++ref.current; return () => id === ref.current; }, []);
}

export function useTenantScope(user) {
  const isSuperAdmin = user?.role === 'super_admin';
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState('');
  useEffect(() => {
    if (!isSuperAdmin) return;
    api.request('/super-admin/tenants')
      .then((list) => {
        const rows = Array.isArray(list) ? list : (list?.tenants || []);
        setTenants(rows);
        setTenantId((current) => current || rows[0]?.id || rows[0]?._id || '');
      })
      .catch(() => setTenants([]));
  }, [isSuperAdmin]);
  const ready = !isSuperAdmin || Boolean(tenantId);
  const query = isSuperAdmin && tenantId ? `tenant_id=${tenantId}` : '';
  const body = isSuperAdmin && tenantId ? { tenant_id: tenantId } : {};
  return { isSuperAdmin, tenants, tenantId, setTenantId, ready, query, body };
}

export const TenantSelect = ({ scope, testid }) => (scope.isSuperAdmin ? (
  <label className="text-sm">
    <span className="mb-1 block text-xs text-muted-foreground">Tenant</span>
    <select value={scope.tenantId} onChange={(e) => scope.setTenantId(e.target.value)}
            className="h-10 rounded-md border bg-background px-3 text-sm" data-testid={testid}>
      {!scope.tenants.length && <option value="">No tenants</option>}
      {scope.tenants.map((t) => (
        <option key={t.id || t._id} value={t.id || t._id}>{t.company_name || t.name || t.subdomain}</option>
      ))}
    </select>
  </label>
) : null);

export async function downloadFile(endpoint, filename) {
  const response = await fetch(`${api.baseURL}${endpoint}`, { headers: api.getHeaders() });
  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try { const data = await response.json(); detail = data.detail?.message || data.detail || detail; } catch { /* binary */ }
    throw new Error(detail);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export const FinanceOnly = ({ testid }) => (
  <div className="mx-auto max-w-xl rounded-xl border bg-card p-10 text-center" data-testid={testid}>
    <h1 className="text-2xl font-semibold">Finance only</h1>
    <p className="mt-2 text-sm text-muted-foreground">
      This screen is available to an ISO owner or the platform finance team.
    </p>
  </div>
);

export const inputCls = 'h-10 w-full rounded-md border bg-background px-3 text-sm';
export const btnPrimary = 'inline-flex h-10 items-center justify-center gap-2 rounded-full bg-primary px-4 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40';
export const btnGhost = 'inline-flex h-10 items-center justify-center gap-2 rounded-full border px-4 text-sm transition-colors hover:bg-muted disabled:opacity-40';
export const btnSmall = 'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs transition-colors hover:bg-muted disabled:opacity-40';
